import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, computed, ElementRef, inject, input, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import * as echarts from 'echarts';
import moment from 'moment';
import { combineLatest, switchMap } from 'rxjs';
import { GenNighttimeLightOutageDays } from '../../../generated/serverModels/GenNighttimeLightOutageDays';
import { AppService } from '../../../services/app.service';

@Component({
  selector: 'tgrid-nighttime-lights-popup',
  imports: [
    MatCard,
    MatCardContent,
    MatCardTitle,
  ],
  templateUrl: './nighttime-lights-popup.component.html',
  styleUrl: './nighttime-lights-popup.component.scss',
})
export class NighttimeLightsPopupComponent {
  private appService = inject(AppService);
  private httpClient = inject(HttpClient);
  public data = input.required<GenNighttimeLightOutageDays>();
  public chartElement = viewChild.required<ElementRef>('chart');

  protected title = computed(() => {
    if (this.data() === undefined) return '';

    return `Outage days for ${this.data().name} (${this.data().stateAbbreviation})`;
  });

  private chart: echarts.ECharts;


  constructor() {
    combineLatest([
      toObservable(this.appService.selectedYears),
      toObservable(this.data),
      toObservable(this.chartElement),
    ]).pipe(
      switchMap(([years, modalData, chartElement]: [number[], GenNighttimeLightOutageDays, ElementRef]) => {
        this.chart = echarts.init(chartElement.nativeElement);
        const params = new HttpParams()
          .set('fips', modalData.fipsCode)
          .set('years', years.join(','));
        return this.httpClient.get<{
          customersOut: number,
          date: string;
        }[]>('api/nighttimelights', { params });
      }),
      takeUntilDestroyed(),
    )
      .subscribe((d) => {
        const xValues: number[] = [];
        const yValues: number[] = [];

        d.forEach(point => {
          yValues.push(point.customersOut);
          xValues.push(moment(point.date).valueOf());
        });


        const options: echarts.EChartsOption = {
          grid: {
            top: 35,
            bottom: 70,
            left: 45,
            right: 45,
          },
          dataZoom: {
            start: 90,
            end: 100,
            right: 60,
            left: 45,
            bottom: 5,
            minSpan: 5,
            dataBackground: {
              areaStyle: {
                color: '#228c22',
              },
              lineStyle: {
                color: '#228c22',
              },
            },
            labelFormatter: (_value: number, valueStr: string) => {
              const val = parseInt(valueStr);
              return moment(val).format('M/DD/YY');
            },
          } as echarts.EChartsOption['dataZoom'],
          tooltip: {
            trigger: 'axis',
            formatter: (param: {marker: string; data: unknown; axisValue: string}[]) => {
              return `
            <p style="text-align: center"> Date: ${moment(parseInt(param[0].axisValue)).format('M/DD/YYYY')}</p>
            <p>Customers Out: ${param[0].data}</p>
            `;
            },
          } as unknown as echarts.EChartsOption['tooltip'],
          xAxis: {
            boundaryGap: false,
            type: 'category',
            data: xValues,
            name: 'Date',
            nameLocation: 'middle',
            nameTextStyle: {
              padding: [10, 0, 0, 0],
            },
            axisLabel: {
              color: '#707070',
              formatter: (date) => {
                const value = parseInt(date);
                return moment(value).format('M/DD/YYYY');
              },
            },
          },
          yAxis: {
            type: 'value',
            name: 'Customers Out',
            nameLocation: 'end',
            nameTextStyle: {
              padding: [0, 0, 0, 0],
            },
          },
          series: [
            {
              name: 'customersOut',
              type: 'line',
              smooth: 0.6,
              itemStyle: {
                color: () => '#228c22',
              },
              areaStyle: {
                opacity: 0.8,
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {
                    offset: 0,
                    color: 'rgb(34, 139, 34)',
                  },
                  {
                    offset: 1,
                    color: 'rgb(255, 255, 255)',
                  },

                ]),
              },
              lineStyle: {
                color: '#228c22',
              },
              data: yValues,
            },
          ],
        };
        this.chart.setOption(options);
      });


  }
}
